import { Transaction } from '@mysten/sui/transactions';
import { mistToDgc } from './amount.js';
import { appendConsolidateLooseCoinsOfType, fetchLooseCoinsOfType, } from './buildConsolidateCpc.js';
import { CPC_FRAMEWORK } from './constants.js';
function appendSendFromAddressBalanceForType(tx, coinType, { recipient, amountMist }) {
    const amount = BigInt(amountMist);
    const withdrawal = tx.withdrawal({ amount, type: coinType });
    const balance = tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::redeem_funds`,
        typeArguments: [coinType],
        arguments: [withdrawal],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [coinType],
        arguments: [balance, tx.pure.address(recipient)],
    });
}
/** P-UX：从地址余额转出任意 `Coin<T>`（Gas 由地址余额支付）。可先把零散 Coin 归集进 AB。 */
export function buildSendFromAddressBalanceForType(coinType, params) {
    const tx = new Transaction();
    if (params.owner && params.looseCoinObjectIds && params.looseCoinObjectIds.length > 0) {
        appendConsolidateLooseCoinsOfType(tx, params.owner, coinType, params.looseCoinObjectIds);
    }
    appendSendFromAddressBalanceForType(tx, coinType, params);
    tx.setGasPayment([]);
    return tx;
}
export async function fetchLooseCoinsForType(client, owner, coinType) {
    const coins = [];
    let cursor = undefined;
    for (;;) {
        const resp = await client.getCoins({ owner, coinType, cursor });
        for (const c of resp.data ?? []) {
            if (BigInt(c.balance) > 0n) {
                coins.push({ coinObjectId: c.coinObjectId, balance: c.balance });
            }
        }
        if (!resp.hasNextPage || !resp.nextCursor)
            break;
        cursor = resp.nextCursor;
    }
    return coins;
}
/** 从零散 `Coin<T>` 合并后 split 并 `public_transfer` 给收款方。 */
export async function buildTransferLooseCoinForOwner(client, owner, params) {
    const amountMist = BigInt(params.amountMist);
    const coins = await fetchLooseCoinsForType(client, owner, params.coinType);
    const total = coins.reduce((s, c) => s + BigInt(c.balance), 0n);
    if (total < amountMist) {
        throw new Error(`余额不足：需 ${mistToDgc(amountMist)}，持有约 ${mistToDgc(total)}`);
    }
    const tx = new Transaction();
    let source = tx.object(coins[0].coinObjectId);
    const single = coins.find((c) => BigInt(c.balance) >= amountMist);
    if (single) {
        source = tx.object(single.coinObjectId);
    }
    else if (coins.length > 1) {
        for (let i = 1; i < coins.length; i++) {
            tx.moveCall({
                target: `${CPC_FRAMEWORK}::coin::join`,
                typeArguments: [params.coinType],
                arguments: [source, tx.object(coins[i].coinObjectId)],
            });
        }
    }
    const [outCoin] = tx.splitCoins(source, [tx.pure.u64(amountMist)]);
    tx.transferObjects([outCoin], tx.pure.address(params.recipient));
    tx.setGasPayment([]);
    return tx;
}
export async function getCoinBalanceView(client, owner, coinType) {
    const bal = await client.getBalance({ owner, coinType });
    const total = BigInt(bal.totalBalance);
    const addressBalance = BigInt(bal.fundsInAddressBalance ?? 0);
    return {
        totalMist: total,
        addressBalanceMist: addressBalance,
        looseCoinMist: total > addressBalance ? total - addressBalance : 0n,
    };
}
/**
 * 智能转账：按「地址余额 + 零散 Coin」合计判断。
 * - 合计够付且存在零散：先归集进 AB，再从 AB 支付（解决 AB 与零散各一截、单独都不够的情况）
 * - 仅零散且合计够：join → split → transfer
 */
export async function buildSendCoinForOwner(client, owner, params) {
    const amountMist = BigInt(params.amountMist);
    const view = await getCoinBalanceView(client, owner, params.coinType);
    if (view.totalMist < amountMist) {
        throw new Error(`余额不足：需 ${mistToDgc(amountMist)}，持有约 ${mistToDgc(view.totalMist)}`);
    }
    const loose = await fetchLooseCoinsOfType(client, owner, params.coinType);
    const looseIds = loose.map((c) => c.coinObjectId);
    // 地址余额单独够，或需要「AB + 零散」拼起来：同一笔 PTB 先归集再从 AB 付出。
    if (view.addressBalanceMist >= amountMist || view.addressBalanceMist > 0n) {
        return buildSendFromAddressBalanceForType(params.coinType, {
            ...params,
            owner,
            looseCoinObjectIds: looseIds,
        });
    }
    // AB=0，纯零散 Coin
    return buildTransferLooseCoinForOwner(client, owner, params);
}
